import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Button,
    IconButton,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Box,
    Container,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const menuItems = [
    { text: 'Home', path: '/' },
    { text: 'Demos', path: '/demos' },
    { text: 'Solutions', path: '/pricing' },
    { text: 'About', path: '/about' },
    { text: 'Starter Websites', path: '/starter-websites' },
];

const BrandWordmark = () => (
    <Box
        component={RouterLink}
        to="/"
        aria-label="DevCraft home"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            textDecoration: 'none',
            fontSize: { xs: '1.5rem', md: '1.7rem' },
            fontWeight: 850,
            letterSpacing: '-0.055em',
            lineHeight: 1,
        }}
    >
        <Box component="span" sx={{ color: 'primary.main' }}>dev</Box>
        <Box component="span" sx={{ color: 'secondary.main' }}>craft</Box>
    </Box>
);

const Navbar = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();

    const navLinkStyles = (path) => ({
        color: location.pathname === path ? 'primary.main' : 'text.primary',
        fontWeight: location.pathname === path ? 750 : 600,
        px: 1.5,
        '&:hover': {
            color: 'primary.main',
            backgroundColor: 'rgba(7, 90, 140, 0.05)',
        },
    });

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                bgcolor: 'rgba(255, 255, 255, 0.94)',
                color: 'text.primary',
                borderBottom: '1px solid rgba(16, 42, 58, 0.08)',
                backdropFilter: 'blur(14px)',
            }}
        >
            <Container maxWidth="lg">
                <Toolbar disableGutters sx={{ minHeight: { xs: 68, md: 76 } }}>
                    <BrandWordmark />
                    <Box sx={{ flexGrow: 1 }} />

                    {isMobile ? (
                        <>
                            <IconButton
                                color="inherit"
                                aria-label={mobileOpen ? 'close navigation' : 'open navigation'}
                                onClick={() => setMobileOpen(true)}
                            >
                                <MenuRoundedIcon />
                            </IconButton>
                            <Drawer
                                variant="temporary"
                                anchor="right"
                                open={mobileOpen}
                                onClose={() => setMobileOpen(false)}
                                ModalProps={{ keepMounted: true }}
                                PaperProps={{ sx: { width: 'min(84vw, 340px)', p: 2 } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <BrandWordmark />
                                    <IconButton aria-label="close navigation" onClick={() => setMobileOpen(false)}>
                                        <CloseRoundedIcon />
                                    </IconButton>
                                </Box>
                                <List>
                                    {menuItems.map((item) => (
                                        <ListItemButton
                                            component={RouterLink}
                                            to={item.path}
                                            key={item.text}
                                            selected={location.pathname === item.path}
                                            onClick={() => setMobileOpen(false)}
                                            sx={{ borderRadius: 2, mb: 0.5 }}
                                        >
                                            <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 650 }} />
                                        </ListItemButton>
                                    ))}
                                </List>
                                <Button
                                    component={RouterLink}
                                    to="/start-website"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    onClick={() => setMobileOpen(false)}
                                    sx={{ mt: 2, py: 1.4, bgcolor: 'primary.main' }}
                                >
                                    Start a project
                                </Button>
                            </Drawer>
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {menuItems.map((item) => (
                                <Button key={item.text} component={RouterLink} to={item.path} sx={navLinkStyles(item.path)}>
                                    {item.text}
                                </Button>
                            ))}
                            <Button
                                component={RouterLink}
                                to="/start-website"
                                variant="contained"
                                sx={{ ml: 1.5, px: 2.4, py: 1.1, bgcolor: 'primary.main' }}
                            >
                                Start a project
                            </Button>
                        </Box>
                    )}
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default Navbar;
